// engine2025/params/registry.ts — F0.T0.3: registro de parámetros fiscales.
//
// Regla dura §0.1 PLAN_IMPLEMENTACION_IRPF.md: todo importe/porcentaje/escala sale de la §4
// del capítulo citado, identificado por su etiqueta P-nnn. Un parámetro bloqueado
// (⚠️VERIFICAR, tabla §6 del plan) se registra con verified:false y lanza
// UnverifiedParamError al usarlo — nunca un valor "razonable".
//
// Diseño (plan §2): el registro se INYECTA en cada `blocks/*.ts` como argumento, nunca se
// importa una instancia global — así un test puede sustituir un parámetro bloqueado por un
// valor de prueba sin tocar el resto del motor.

/** Cita de origen de un parámetro: capítulo de la guía + página del manual AEAT. */
export interface ParamSource {
    /** Ej.: "Cap.14 — 14-minimo-personal-familiar.md". */
    chapter: string;
    /** Ej.: "p. 1068". */
    page: string;
}

export interface ParamDefinition<T> {
    id: string;
    concept: string;
    value: T | null;
    verified: boolean;
    source: ParamSource | null;
}

export interface DefineParamOptions {
    concept: string;
    verified: boolean;
    source: ParamSource | null;
}

/** Lanzado por `ParamRegistry.get` cuando el parámetro solicitado no está verificado. */
export class UnverifiedParamError extends Error {
    constructor(public readonly paramId: string, concept: string) {
        super(
            `Parámetro fiscal "${paramId}" (${concept}) no está verificado. ` +
            'Prohibido aproximar un valor fiscal (regla dura §0.1 PLAN_IMPLEMENTACION_IRPF.md); ' +
            'desbloquéalo primero (ver §6 del plan / docs/guia-renta-2025/parametros-verificados.md).'
        );
        this.name = 'UnverifiedParamError';
    }
}

export class ParamRegistry {
    private readonly params = new Map<string, ParamDefinition<unknown>>();

    /** Registra un parámetro. `verified:true` exige `value` y `source` no nulos. */
    define<T>(id: string, value: T | null, opts: DefineParamOptions): void {
        if (this.params.has(id)) {
            throw new Error(`Parámetro duplicado: "${id}" ya está registrado.`);
        }
        if (opts.verified && (value === null || opts.source === null)) {
            throw new Error(
                `Parámetro "${id}" declarado verified:true pero sin value/source. ` +
                'Un parámetro confirmado siempre lleva su cita de capítulo+página.'
            );
        }
        this.params.set(id, { id, value, verified: opts.verified, source: opts.source, concept: opts.concept });
    }

    /** Devuelve el valor confirmado. Lanza `UnverifiedParamError` si `verified:false`. */
    get<T>(id: string): T {
        const def = this.params.get(id);
        if (!def) {
            throw new Error(`Parámetro desconocido: "${id}". Regístralo con ParamRegistry.define antes de usarlo.`);
        }
        if (!def.verified || def.value === null) {
            throw new UnverifiedParamError(id, def.concept);
        }
        return def.value as T;
    }

    /** Acceso a la definición completa (metadatos), sin lanzar si no está verificada. */
    getDefinition(id: string): ParamDefinition<unknown> | undefined {
        return this.params.get(id);
    }

    /** Todas las definiciones registradas (para informes de cobertura, F15.T15.4). */
    all(): ParamDefinition<unknown>[] {
        return Array.from(this.params.values());
    }
}
